const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');

const TOOLTIP = 'Generates a menu and page access permissions form based on Customizable Visibility - Access Modifiers';

function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged horizontally (this is the default)
    //Meaning each (dummy)input continues on the top line followed by  the access modifiers
    blocklyBlock.setInputsInline(false);
    line1OfInputs.appendField('  Property Name:').appendField(new Blockly.FieldTextInput('access'), 'propertyName');
    line1OfInputs.appendField('  Tooltip:').appendField(new Blockly.FieldTextInput(''), 'tooltip');

    //line 2 of inputs
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Default Access Property Name:')
        .appendField(new Blockly.FieldTextInput('defaultAccess'), 'defaultAccessPropertyName');
}

function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    addCodeProperties(['propertyName', 'tooltip', 'defaultAccessPropertyName']);
    //nested blocks in the statements are added automatically by blockly
    return code;
}

/** Builds the TypeBox schema fragment for the PageAccess form element. */
function asDataDictionary(hNode, context) {
    const { id, title, propertyName, defaultAccessPropertyName = 'defaultAccess' } = hNode;
    //This block tells us nothing about namespaces/relations
    const subDataDictionary = {};

    //this block tells us about this current property
    const isOptional = hNode.hasOwnProperty('optional') ? hNode.optional : undefined;
    const editable = !context.readOnly ? true : undefined;

    // Helper to create boolean schema
    const createBoolean = (propName, propTitle, isRequired) => {
        const boolSchema = Type.Boolean({
            title: propTitle,
            'x-dataType': 'boolean',
            'x-propertyName': propName,
            'x-optional': !isRequired,
            ...(editable !== undefined && { 'x-editable': editable })
        });
        return isRequired ? boolSchema : Type.Optional(boolSchema);
    };

    // Create <hNodeId> schema (dynamic boolean property)
    const hNodeIdSchema = Type.Optional(
        Type.Boolean({
            title: 'Allow Access on the given hNode (action or tab)',
            'x-dataType': 'boolean',
            'x-propertyName': '<hNodeId>',
            'x-variableName': true,
            'x-optional': true,
            ...(editable !== undefined && { 'x-editable': editable })
        })
    );

    // Create <pageId> schema (dynamic object property)
    const pageIdSchema = Type.Optional(
        Type.Object(
            {
                mobile: createBoolean('mobile', 'Allow Access on Mobile', true),
                desktop: createBoolean('desktop', 'Allow Access on Desktop', true),
                tablet: createBoolean('tablet', 'Allow Access on Tablet, defaults to the Desktop setting if not set', false),
                '<hNodeId>': hNodeIdSchema
            },
            {
                title: 'Access to Page',
                'x-dataType': 'object',
                'x-propertyName': '<pageId>',
                'x-variableName': true,
                'x-optional': true,
                ...(editable !== undefined && { 'x-editable': editable }),
                additionalProperties: true
            }
        )
    );

    // Create rights object
    const rightsSchema = Type.Optional(
        Type.Object(
            {
                '<pageId>': pageIdSchema
            },
            {
                title: title,
                'x-dataType': 'object',
                'x-propertyName': 'rights',
                'x-optional': true,
                ...(editable !== undefined && { 'x-editable': editable }),
                additionalProperties: true
            }
        )
    );

    // Create defaultAccess boolean
    const defaultAccessSchema = Type.Optional(
        Type.Boolean({
            title: 'Automatically Display New Functionality',
            'x-dataType': 'boolean',
            'x-propertyName': defaultAccessPropertyName,
            'x-optional': true,
            ...(editable !== undefined && { 'x-editable': editable })
        })
    );

    // Create the main PageAccess object
    const pageAccessSchema = Type.Object(
        {
            [defaultAccessPropertyName]: defaultAccessSchema,
            rights: rightsSchema
        },
        {
            'x-id': id,
            title: title,
            'x-dataType': 'object',
            'x-propertyName': propertyName,
            ...(isOptional !== undefined && { 'x-optional': isOptional }),
            ...(editable !== undefined && { 'x-editable': editable }),
            additionalProperties: true
        }
    );

    // a PageAccess component returns the following format:
    const partialTypeDefinition = {
        [propertyName]: isOptional ? Type.Optional(pageAccessSchema) : pageAccessSchema
    };

    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
